import { createBrowserClient } from "@supabase/ssr";
import { getSupabaseCookieOptions } from "@/utils/mobile-detection";
import { IS_DEMO_BUILD } from "./config";

const getCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;

  const match = document.cookie.match(
    new RegExp(
      "(?:^|; )" +
        name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, "\\$1") +
        "=([^;]*)",
    ),
  );

  return match ? decodeURIComponent(match[1]) : null;
};

type ClientDatabaseEnvironment = "local" | "production" | "demo";

let hostOnlyAuthCookiesCleared = false;

/**
 * Before SEC-58 the Supabase auth cookies were host-only (no Domain). Now they are
 * written on the root domain, so a browser that signed in before can hold two
 * cookies with the same name. The client reads the stale host-only one, fails to
 * refresh it and removes the session with the root domain, which leaves the
 * host-only cookie in place: it loads it again and loops against
 * /auth/v1/token until Supabase answers 429. Deleting without a Domain attribute
 * only removes the host-only copies; the root-domain session is untouched.
 */
const clearHostOnlyAuthCookies = () => {
  if (hostOnlyAuthCookiesCleared || typeof document === "undefined") return;
  hostOnlyAuthCookiesCleared = true;

  document.cookie
    .split("; ")
    .map((cookie) => cookie.split("=")[0])
    .filter((name) => /^sb-.+-auth-token(\.\d+)?$/.test(name))
    .forEach((name) => {
      document.cookie = `${name}=; Max-Age=0; path=/`;
    });
};

export const createClient = () => {
  const isProdBuild = process.env.NODE_ENV === "production";
  const dbEnvCookie = getCookie("db_env");
  const isValidCookieEnv =
    dbEnvCookie === "local" ||
    dbEnvCookie === "production" ||
    dbEnvCookie === "demo";

  // The demo build is pinned to the demo database (see IS_DEMO_BUILD).
  const env: ClientDatabaseEnvironment = IS_DEMO_BUILD
    ? "demo"
    : isProdBuild
    ? "production"
    : isValidCookieEnv
      ? (dbEnvCookie as ClientDatabaseEnvironment)
      : "local";

  const supabaseUrl =
    env === "production"
      ? process.env.NEXT_PUBLIC_SUPABASE_URL_PROD
      : env === "demo"
        ? process.env.NEXT_PUBLIC_SUPABASE_URL_DEMO
        : process.env.NEXT_PUBLIC_SUPABASE_URL_LOCAL;
  const supabaseKey =
    env === "production"
      ? process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY_PROD
      : env === "demo"
        ? process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY_DEMO
        : process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY_LOCAL;

  if (!supabaseUrl || !supabaseKey) {
    const whereToSet =
      env === "production"
        ? "production environment"
        : ".env.development or .env.local";

    const missingVarsByEnv: Record<ClientDatabaseEnvironment, string> = {
      production:
        "- NEXT_PUBLIC_SUPABASE_URL_PROD\n- NEXT_PUBLIC_SUPABASE_ANON_KEY_PROD",
      demo: "- NEXT_PUBLIC_SUPABASE_URL_DEMO\n- NEXT_PUBLIC_SUPABASE_ANON_KEY_DEMO",
      local:
        "- NEXT_PUBLIC_SUPABASE_URL_LOCAL\n- NEXT_PUBLIC_SUPABASE_ANON_KEY_LOCAL",
    };

    throw new Error(
      "Missing Supabase environment variables!\n" +
        `Please check your ${whereToSet}:\n` +
        missingVarsByEnv[env],
    );
  }

  // Same cookie domain as the server client, so both write the same cookies.
  const cookieOptions = getSupabaseCookieOptions(
    typeof window === "undefined" ? undefined : window.location.host,
  );

  // Only when cookies live on the root domain; host-only ones are then leftovers.
  if ("domain" in cookieOptions) {
    clearHostOnlyAuthCookies();
  }

  return createBrowserClient(supabaseUrl, supabaseKey, { cookieOptions });
};
